import { LoaderFileKeys } from "../app/constant/AppConstants";
import { OptionsDataManager } from "../app/managers/OptionsDataManager";
import { AppSettings } from "../app/managers/AppSettings";
import { SmartMask } from "../mask/SmartMask";
import { Showable } from "../mixins/Showable";
import { Padding } from "../utils/Padding";
import { RectSize } from "../utils/RectSize";
import { SmartCull, SmartCullMode } from "../utils/SmartCull";
import { ScrollPaneContent } from "./scrollpane/ScrollPaneContent";
import { AppTimings } from "../app/data/AppConstants";


export enum ScrollAxis {
    HORIZONTAL,
    VERTICAL,
}

export enum ScrollPaneEvents {
    SCROLL_AMOUNT_CHANGE = "scroll+amount_change",
}

// linting
export interface ScrollPane extends Showable { }

export class ScrollPane
    extends Phaser.GameObjects.Container {
    private static appliedMixins: boolean = false;
    /**
     * Need this to consume mouse up event (so that it doesnt fire on content items)
     */
    public overlay: Phaser.GameObjects.Image;
    public content: ScrollPaneContent;
    public contentMask: SmartMask;
    public scrollAnywhere: boolean = false;
    /**
     * Beyond content borders, for ex if on vertical scrollbar left and right from content is empty (not content), scroll from there too?
     */
    public scrollOnSides: boolean = true;
    // custom culler (cull container items that are off screen)
    public cullItems: boolean;

    protected size: RectSize;
    protected cachedOverlayHitArea: Phaser.Geom.Rectangle;
    /**
     * Only start monitoring drag once primaryButton is downed over overlay area
     * (this is point intersect check and overlay is not interactive at that point)
     */
    protected monitorDrag: boolean = false;
    /**
     * Min distance for drag to be considered DRAG and not a CLICK over panned content
     * In reality, cur axis distance travelled is checked OnMove and when considered DRAG,
     * overlay mouse event consumption is activated.
     */
    protected minDragDistance: number = 10;
    /**
     * Current drag distance achieved on relevant axis
     */
    protected curAxisDragDistance: number = 0;
    protected scrollAxis: ScrollAxis;
    protected lastScrollDelta: Phaser.Math.Vector2;
    protected excessScrollAmount: Padding;
    protected isOverlayInteractive: boolean = false;
    // cache
    protected curScrollAmount: Phaser.Math.Vector2;
    /**
     * Only emits scroll_change if vector changes (going against bounds using mouse wheel for ex doesnt trigger change)
     */
    protected prevScrollAmount: Phaser.Math.Vector2;


    public Init(cullItems: boolean = true): void {
        this.size = new RectSize();

        this.cullItems = cullItems;

        this.cachedOverlayHitArea = new Phaser.Geom.Rectangle();
        this.lastScrollDelta = new Phaser.Math.Vector2();
        this.curScrollAmount = new Phaser.Math.Vector2();
        this.prevScrollAmount = new Phaser.Math.Vector2();
    }

    public Build(): void {
        this.overlay = this.scene.make.image({
            key: LoaderFileKeys.ui,
            frame: "rect-2x2-transparent.png",
            // DEBUG overlay
            // frame: "rect-2x2-white.png",
            // alpha: 0.5,
        });
        // this.overlay.setOrigin(0);
        this.overlay.setOrigin(0.5, 0);
        this.SetOverlayInteractive(false);
    }

    public SetExcessScrollAmount(value: Padding, snapToTop: boolean = true): void {
        this.excessScrollAmount = value;
        if (snapToTop) {
            this.content.y = this.contentMask.y + this.excessScrollAmount.top;
        }
    }

    public RegisterListeners(): void {
        // console.log("pane: reg");
        this.scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.OnPointerDown, this);
        this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.OnPointerMove, this);
        this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.OnPointerUp, this);
        this.scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.OnPointerUpOutside, this);
        this.scene.input.on(Phaser.Input.Events.GAMEOBJECT_POINTER_WHEEL, this.OnGameObjectPointerWheel, this);
    }

    public UnregisterListeners(): void {
        // console.log("pane: unreg");
        this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.OnPointerDown, this);
        this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.OnPointerMove, this);
        this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.OnPointerUp, this);
        this.scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.OnPointerUpOutside, this);
        this.scene.input.off(Phaser.Input.Events.GAMEOBJECT_POINTER_WHEEL, this.OnGameObjectPointerWheel, this);

        // need to stop drag monitoring if currently underway
        this.StopDrag();
    }

    // https://newdocs.phaser.io/docs/3.54.0/Phaser.Input.Events.POINTER_DOWN
    protected OnPointerDown(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]): void {
        if (pointer.primaryDown) {
            // kill scroll animation, if ongoing
            this.scene.tweens.killTweensOf(this.content);
            // 
            if (this.scrollAnywhere || this.IsPointOverOverlay(pointer.x, pointer.y)) {
                this.monitorDrag = true;
            }
        }
    }

    protected OnPointerMove(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]): void {
        if (this.monitorDrag) {
            // kill scroll animation, if ongoing
            this.scene.tweens.killTweensOf(this.content);
            
            // subclass might need to set some flags at this point 
            // that notify it about 'scroll from drag'
            this.PreOnScrollDrag();
            
            if (this.scrollAxis == ScrollAxis.HORIZONTAL) {
                this.lastScrollDelta.set(pointer.position.x - pointer.prevPosition.x, 0);
                this.curAxisDragDistance += this.lastScrollDelta.x;
            }
            else {
                this.lastScrollDelta.set(0, pointer.position.y - pointer.prevPosition.y);
                this.curAxisDragDistance += this.lastScrollDelta.y;
            }
            this.ScrollByAmount(this.lastScrollDelta);
            // console.log(Math.abs(this.curAxisDragDistance) > this.minDragDistance, this.IsOverlayInteractive(), this.curAxisDragDistance, this.minDragDistance)

            // drag, not a click
            if (Math.abs(this.curAxisDragDistance) > this.minDragDistance) {
                // if overlay not yet consuming events
                if (!this.IsOverlayInteractive()) {
                    // make it consume events to prevent UI triggering on MouseUp
                    this.SetOverlayInteractive(true);
                }
            }
            // click, not a drag
            else {
                // if overlay is consuming events
                if (this.IsOverlayInteractive()) {
                    // make it stop consuming events to allow for click-throughs
                    this.SetOverlayInteractive(false);
                }
            }
        }
    }
    
    protected PreOnScrollDrag(): void {
        // override in subclass
    }

    /**
     * Update scrollbar to keep content within bounds
     * @param forceUpdate force update cull items (on orient switch for ex or resize this should be true)
     */
    public UpdateInPlace(forceUpdate: boolean = false): void {
        // console.log("UpdateInPlace");

        this.lastScrollDelta.set(0);
        this.ScrollByAmount(this.lastScrollDelta, forceUpdate);
    }

    /**
     * 
     * @param forceUpdate force update cull items (on orient switch for ex or resize this should be true)
     */
    public ScrollByAmount(scrollAmount: Phaser.Math.Vector2, forceUpdate: boolean = false): void {
        this.curScrollAmount.set(this.content.x + scrollAmount.x, this.content.y + scrollAmount.y);
        this.SetScroll(this.curScrollAmount, forceUpdate);
    }

    /**
     * Box curScrollAmount and cull items.
     * @param forceUpdate force update cull items (on orient switch for ex or resize this should be true)
     */
    public SetScroll(
        scrollValue: Phaser.Math.Vector2, 
        forceUpdate: boolean = false, 
        animate: boolean = false,
    ): void {
        const contentSize = this.content.GetSize();
        if (this.scrollAxis == ScrollAxis.VERTICAL) {
            // if mask is bigger than content
            if (this.contentMask.height > contentSize.height) {
                // snap content to top of mask
                this.curScrollAmount.y = this.contentMask.y + this.excessScrollAmount.top;
            }
            else {
                // scroll content, within limits
                // scroll below TOP
                if (scrollValue.y > this.contentMask.y + this.excessScrollAmount.top) {
                    // snap to TOP
                    this.content.y = this.contentMask.y + this.excessScrollAmount.top;
                    this.curScrollAmount.y = this.contentMask.y + this.excessScrollAmount.top;
                }
                // scroll under BOTTOM
                else if (scrollValue.y + contentSize.height + this.excessScrollAmount.bottom < this.contentMask.y + this.contentMask.height) {
                    // snap to BOTTOM
                    this.curScrollAmount.y = this.contentMask.y + this.contentMask.height - contentSize.height - this.excessScrollAmount.bottom;
                }
                // scrolling within limits
                else {
                    // snap to target position
                    this.curScrollAmount.y = scrollValue.y;
                }
            }
        }

        // console.log(forceUpdate, this.curScrollAmount.y, this.prevScrollAmount.y);
        if (forceUpdate || !this.curScrollAmount.equals(this.prevScrollAmount)) {
            this.prevScrollAmount.copy(this.curScrollAmount);

            this.scene.tweens.killTweensOf(this.content);
            if (animate) {
                // let duration = OptionsDataManager.GetTiming(AppTimings.scrollableContentScrollDuration);
                // EDIT: scroll duration same regardless of gameSpeed
                let duration = AppTimings.scrollableContentScrollDuration;
                const ease = AppSettings.ease.scrollableContentScrollEase;
                this.scene.tweens.add({
                    targets: this.content,
                    y: this.curScrollAmount.y,
                    duration: duration,
                    ease: ease,
                    onUpdate: ()=>{
                        if (this.cullItems) {
                            this.UpdateCullItems();
                        }
                        this.OnScrollAmountChange();
                        // this.emit(ScrollPaneEvents.SCROLL_AMOUNT_CHANGE, this.curScrollAmount);
                    },
                });
            }
            else {
                this.content.y = this.curScrollAmount.y;
            }

            if (this.cullItems) {
                // update cull
                this.UpdateCullItems();
            }

            this.OnScrollAmountChange();
            this.emit(ScrollPaneEvents.SCROLL_AMOUNT_CHANGE, this.curScrollAmount);
        }
    }

    protected OnScrollAmountChange(): void {
        // override in subclass
    }

    public UpdateCullItems(): void {
        let allItems: Phaser.GameObjects.GameObject[] = [];
        this.content.GetCullables(this.content, allItems);
        // console.log(allItems.length);
        const thisTM = this.getWorldTransformMatrix();
        const globalOverlayRect = new Phaser.Geom.Rectangle(
            thisTM.tx + this.cachedOverlayHitArea.x,
            thisTM.ty + this.cachedOverlayHitArea.y,
            this.cachedOverlayHitArea.width,
            this.cachedOverlayHitArea.height,
        );
        SmartCull.Cull(globalOverlayRect, allItems, SmartCullMode.ALPHA);
    }

    // https://newdocs.phaser.io/docs/3.54.0/Phaser.Input.Events.POINTER_UP
    protected OnPointerUp(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]): void {
        // console.log("up", pointer);
        this.StopDrag();
    }

    protected OnPointerUpOutside(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]): void {
        // console.log("OnPointerUpOutside");
        this.StopDrag();
    }

    protected OnGameObjectPointerWheel(pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]): void {
        // console.log("OnGameObjectPointerWheel", pointer.deltaY);

        // subclass might need to set some flags at this point 
        // that notify it about 'scroll from wheel input'
        this.PreOnGameObjectPointerWheel();

        if (ScrollAxis.HORIZONTAL) {
            this.lastScrollDelta.set(-pointer.deltaY, 0);
            this.ScrollByAmount(this.lastScrollDelta);
        }
        else {
            this.lastScrollDelta.set(0, -pointer.deltaY);
            this.ScrollByAmount(this.lastScrollDelta);
        }
    }

    protected PreOnGameObjectPointerWheel() {
        // override in subclass
    }

    private StopDrag(): void {
        this.monitorDrag = false;
        this.curAxisDragDistance = 0;
        // if overlay is consuming events
        if (this.IsOverlayInteractive()) {
            // make it stop consuming events to allow for click-throughs
            this.SetOverlayInteractive(false);
        }
    }

    private IsPointOverOverlay(x: number, y: number): boolean {
        const pointerPoint = new Phaser.Geom.Point(x, y);
        const thisTM = this.getWorldTransformMatrix();
        const globalOverlayRect = new Phaser.Geom.Rectangle(
            thisTM.tx + this.cachedOverlayHitArea.x,
            thisTM.ty + this.cachedOverlayHitArea.y,
            this.cachedOverlayHitArea.width,
            this.cachedOverlayHitArea.height
        );
        // console.log(thisTM.tx,thisTM.ty);
        return Phaser.Geom.Rectangle.ContainsPoint(globalOverlayRect, pointerPoint);
    }

    public SetSize(width: number, height: number): void {
        this.size.Set(width, height);

        // stretch scrollable area to cover content size
        // BELOTE FIX: actually stretch to infinity HORIZONTALLY (for vertical scroll)
        const infiniteSidesMult = this.scrollOnSides ? 4 : 1;
        this.cachedOverlayHitArea.setSize(width * infiniteSidesMult, height);
        this.cachedOverlayHitArea.x = (width - this.cachedOverlayHitArea.width) * 0.5;
        
        this.overlay.x = width * 0.5;
        this.overlay.setDisplaySize(width, height);

        // override in subclass
    }

    /**
     * Sets interactive on overlay (on|off) or updates hitArea on overlay 
     * @param interactive 
     */
    protected SetOverlayInteractive(
        interactive: boolean = undefined,
        // updateSize: boolean = undefined
    ): void {
        if (interactive == true) {
            if (!this.isOverlayInteractive) {
                this.overlay.setInteractive();
                this.isOverlayInteractive = true;
            }
        }
        else {
            if (interactive == false) {
                if (this.isOverlayInteractive) {
                    this.overlay.disableInteractive();
                    this.isOverlayInteractive = false;
                }
            }
        }
    }

    protected IsOverlayInteractive(): boolean {
        return this.isOverlayInteractive;
    }
}